import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import api from 'services/api';
import history from 'services/history';
import { updateFuncionario } from 'services/usuarios';
import validarCPF from 'validar-cpf';

const SESSION_DURATION_MS = {
  patient: 1000 * 60 * 60 * 4, // 4 horas
  doctor: 1000 * 60 * 60 * 12, // 12 horas
  func: 1000 * 60 * 60 * 12, // 12 horas
};

export function getTodayDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildSessionPayload(user, token, role) {
  const duration = SESSION_DURATION_MS[role] ?? 1000 * 60 * 60 * 12;
  return {
    user,
    token,
    expiresAt: Date.now() + duration,
    loginDate: getTodayDateKey(),
  };
}

const initialState = {
  patient: null,
  doctor: null,
  func: null,
  loading: {
    patient: false,
    doctor: false,
    func: false,
  },
};

export const signInPatient = createAsyncThunk(
  'auth/signInPatient',
  async ({ cpf, senha }, { rejectWithValue }) => {
    if (!validarCPF(cpf)) {
      toast.error('CPF inválido!');
      return rejectWithValue('CPF inválido');
    }
    try {
      const { data } = await api.post('/paciente/login', { cpf, senha });
      const { token, paciente } = data;
      history.push('/dashboardpac');
      return buildSessionPayload(paciente, token, 'patient');
    } catch (error) {
      toast.error(error.response?.data?.error ?? 'Falha no login.');
      return rejectWithValue(error);
    }
  }
);

export const signInDoctor = createAsyncThunk(
  'auth/signInDoctor',
  async ({ cpf, senha }, { rejectWithValue }) => {
    if (!validarCPF(cpf)) {
      toast.error('CPF inválido.');
      return rejectWithValue('CPF inválido');
    }
    try {
      const { data } = await api.post('msessions', { cpf, senha });
      const { token, medico } = data;
      history.push('/agenda');
      return buildSessionPayload(medico, token, 'doctor');
    } catch (error) {
      const msg = error.response?.data?.error ?? 'Falha na autenticação do Médico.';
      toast.error(msg);
      return rejectWithValue(error);
    }
  }
);

export const signInFunc = createAsyncThunk(
  'auth/signInFunc',
  async ({ cpf, senha }, { rejectWithValue }) => {
    if (!validarCPF(cpf)) {
      toast.error('CPF inválido.');
      return rejectWithValue('CPF inválido');
    }
    try {
      const { data } = await api.post('fsessions', { cpf, senha });
      const { token, funcionario } = data;
      history.push('/agenda');
      return buildSessionPayload(funcionario, token, 'func');
    } catch (error) {
      toast.error(error.response?.data?.error ?? 'Falha na autenticação.');
      return rejectWithValue(error);
    }
  }
);

export const updateFunc = createAsyncThunk(
  'auth/updateFunc',
  async (payload, { getState, rejectWithValue }) => {
    const { auth } = getState();
    if (!auth.func?.user) return rejectWithValue('Não autenticado');
    try {
      const updated = await updateFuncionario(auth.func.user.codFunc, payload);
      return { ...auth.func.user, ...updated };
    } catch (error) {
      const msg = error.response?.data?.error ?? 'Erro ao atualizar dados do funcionário.';
      toast.error(msg);
      return rejectWithValue(error);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearPatient: (state) => {
      state.patient = null;
      state.loading.patient = false;
    },
    clearDoctor: (state) => {
      state.doctor = null;
      state.loading.doctor = false;
    },
    clearFunc: (state) => {
      state.func = null;
      state.loading.func = false;
    },
    clearAllAuth: (state) => {
      state.patient = null;
      state.doctor = null;
      state.func = null;
      state.loading = {
        patient: false,
        doctor: false,
        func: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInPatient.pending, (state) => {
        state.loading.patient = true;
      })
      .addCase(signInPatient.fulfilled, (state, action) => {
        state.loading.patient = false;
        state.patient = action.payload;
      })
      .addCase(signInPatient.rejected, (state) => {
        state.loading.patient = false;
      })
      .addCase(signInDoctor.pending, (state) => {
        state.loading.doctor = true;
      })
      .addCase(signInDoctor.fulfilled, (state, action) => {
        state.loading.doctor = false;
        state.doctor = action.payload;
      })
      .addCase(signInDoctor.rejected, (state) => {
        state.loading.doctor = false;
      })
      .addCase(signInFunc.pending, (state) => {
        state.loading.func = true;
      })
      .addCase(signInFunc.fulfilled, (state, action) => {
        state.loading.func = false;
        state.func = action.payload;
      })
      .addCase(signInFunc.rejected, (state) => {
        state.loading.func = false;
      })
      .addCase(updateFunc.pending, (state) => {
        state.loading.func = true;
      })
      .addCase(updateFunc.fulfilled, (state, action) => {
        state.loading.func = false;
        state.func = {
          ...state.func,
          user: action.payload,
          token: state.func?.token,
        };
      })
      .addCase(updateFunc.rejected, (state) => {
        state.loading.func = false;
      });
  },
});

export const { clearPatient, clearDoctor, clearFunc, clearAllAuth } = authSlice.actions;

export const signOutPatient = () => (dispatch) => {
  dispatch(clearPatient());
  history.push('/');
};

export const signOutDoctor = () => (dispatch) => {
  dispatch(clearDoctor());
  history.push('/medico/login');
};

export const signOutFunc = () => (dispatch) => {
  dispatch(clearFunc());
  history.push('/funcionario/login');
};

export const selectPatient = (state) => state.auth.patient?.user ?? null;
export const selectDoctor = (state) => state.auth.doctor?.user ?? null;
export const selectFunc = (state) => state.auth.func?.user ?? null;
export const selectPatientLoading = (state) => state.auth.loading.patient;
export const selectDoctorLoading = (state) => state.auth.loading.doctor;
export const selectFuncLoading = (state) => state.auth.loading.func;

export default authSlice.reducer;
